import React, { useCallback } from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { LazySuspense } from 'react-loosely-lazy';

import { graphql, usePreloadedQuery } from 'react-relay';
import ErrorBoundary from 'view/error-boundary/error-boundary';
import { PaperContent } from 'view/layout/paper';
import { ScreenName } from '@atlassian/help-center-common-component/constants';
import { ErrorMessage } from '@atlassian/help-center-common-component/error-message';
import { Loading } from '@atlassian/help-center-common-component/loading';
import type { FetchError } from '@atlassian/help-center-common-util/fetch';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';
import { useUFOComponentExperience } from '@atlassian/ufo';
import type { topicQuery } from './__generated__/topicQuery.graphql';
import HelpCenterTopic from './help-center-topic';
import { topicViewExperience } from './help-center-topic/experiences';
import messages from './messages';
import type { Props } from './types';

const Topic: React.FC<InjectedIntlProps & Props> = ({ intl, queryReference, topicId }): JSX.Element => {
    const data = usePreloadedQuery<topicQuery>(
        graphql`
            query topicQuery($cloudId: ID!, $helpCenterAri: ID!, $topicId: ID!) {
                ...helpCenterTopic_HelpCenterQuery
            }
        `,
        queryReference
    );
    useUFOComponentExperience(topicViewExperience);
    const markExperienceFailure = useCallback((error: FetchError) => {
        if (!isFetchNetworkOrClientError(error)) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            topicViewExperience.failure({
                metadata: {
                    error: error.message,
                },
            });
        }
    }, []);
    const renderError = useCallback(() => {
        return (
            <ErrorMessage
                title={intl.formatMessage(messages.genericHeading)}
                message={intl.formatMessage(messages.genericMessage)}
            />
        );
    }, [intl]);
    return (
        <PaperContent>
            <ErrorBoundary
                id={ScreenName.VIEW_TOPIC}
                packageName="topics"
                onError={markExperienceFailure}
                renderError={renderError}
            >
                <LazySuspense fallback={<Loading />}>
                    <HelpCenterTopic helpCenter={data} id={topicId} />
                </LazySuspense>
            </ErrorBoundary>
        </PaperContent>
    );
};

export default injectIntl(Topic);
